import { useState } from "react";
import axios from "axios";
import { getPreviousMonthValue } from "../../components/admin/MonthSelect";
import { getBillingDownloadFilename } from "./useAdminBilling";

const initialDocumentFilters = {
  month: getPreviousMonthValue(),
  company: "",
  min_amount: "",
  max_amount: "",
  include_deleted: false,
};

const initialReceiptDocumentFilters = {
  month: getPreviousMonthValue(),
  company: "",
  min_amount: "",
  max_amount: "",
  include_deleted: false,
};

const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const appendDocumentFilterParams = (params, filters) => {
  if (filters.document_type) params.append("document_type", filters.document_type);
  if (filters.month) params.append("month", filters.month);
  if (filters.company) params.append("company", filters.company);
  if (filters.min_amount) params.append("min_amount", filters.min_amount);
  if (filters.max_amount) params.append("max_amount", filters.max_amount);
  if (filters.include_deleted) params.append("include_deleted", "true");
};

const getBlobErrorMessage = async (error, fallbackMessage) => {
  if (error.response?.data instanceof Blob) {
    try {
      const text = await error.response.data.text();
      const errorData = JSON.parse(text);
      return errorData.detail || fallbackMessage;
    } catch (parseError) {
      return error.message || fallbackMessage;
    }
  }
  return error.response?.data?.detail || error.message || fallbackMessage;
};

export default function useAdminBillingDocuments({ apiBaseUrl, setLoading }) {
  const [billingDocumentFilters, setBillingDocumentFilters] = useState(initialDocumentFilters);
  const [receiptDocumentFilters, setReceiptDocumentFilters] = useState(initialReceiptDocumentFilters);
  const [billingDocuments, setBillingDocuments] = useState([]);
  const [receiptDocuments, setReceiptDocuments] = useState([]);
  const [billingDocumentSummary, setBillingDocumentSummary] = useState({
    count: 0,
    active_count: 0,
    deleted_count: 0,
    active_total_amount: 0,
  });
  const [receiptDocumentSummary, setReceiptDocumentSummary] = useState({
    count: 0,
    active_count: 0,
    deleted_count: 0,
    active_total_amount: 0,
  });
  const [billingDocumentsLoading, setBillingDocumentsLoading] = useState(false);
  const [receiptDocumentsLoading, setReceiptDocumentsLoading] = useState(false);

  const fetchBillingDocuments = async (filters = billingDocumentFilters) => {
    setBillingDocumentsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("document_type", "invoice");
      appendDocumentFilterParams(params, filters);
      const response = await axios.get(`${apiBaseUrl}/admin/billing-documents?${params.toString()}`);
      setBillingDocuments(response.data.documents || []);
      setBillingDocumentSummary(response.data.summary || {});
    } catch (error) {
      console.error("請求書一覧の取得に失敗:", error);
      alert("請求書一覧の取得に失敗しました");
    } finally {
      setBillingDocumentsLoading(false);
    }
  };

  const fetchReceiptDocuments = async (filters = receiptDocumentFilters) => {
    setReceiptDocumentsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("document_type", "receipt");
      appendDocumentFilterParams(params, filters);
      const response = await axios.get(`${apiBaseUrl}/admin/billing-documents?${params.toString()}`);
      setReceiptDocuments(response.data.documents || []);
      setReceiptDocumentSummary(response.data.summary || {});
    } catch (error) {
      console.error("領収書一覧の取得に失敗:", error);
      alert("領収書一覧の取得に失敗しました");
    } finally {
      setReceiptDocumentsLoading(false);
    }
  };

  const handleBillingDocumentFiltersChange = (nextFilters) => {
    setBillingDocumentFilters(nextFilters);
  };

  const handleReceiptDocumentFiltersChange = (nextFilters) => {
    setReceiptDocumentFilters(nextFilters);
  };

  const handleDeleteBillingDocument = async (documentId) => {
    if (!window.confirm("この書類を削除しますか？後で復元できます。")) return;
    setLoading(true);
    try {
      await axios.delete(`${apiBaseUrl}/admin/billing-documents/${documentId}`);
      await fetchBillingDocuments();
      await fetchReceiptDocuments();
    } catch (error) {
      console.error("書類の削除に失敗:", error);
      alert("書類の削除に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreBillingDocument = async (documentId) => {
    setLoading(true);
    try {
      await axios.post(`${apiBaseUrl}/admin/billing-documents/${documentId}/restore`);
      await fetchBillingDocuments();
      await fetchReceiptDocuments();
    } catch (error) {
      console.error("書類の復元に失敗:", error);
      alert("書類の復元に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadBillingDocument = async (documentRecord) => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/billing-documents/${documentRecord.id}/download`, {
        responseType: "blob",
      });
      const filename = getBillingDownloadFilename(
        response,
        documentRecord.download_filename || `${documentRecord.document_no}.pdf`,
      );
      triggerBlobDownload(new Blob([response.data], { type: "application/pdf" }), filename);
    } catch (error) {
      console.error("PDFのダウンロードに失敗:", error);
      alert("PDFのダウンロードに失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const handleExportBillingDocuments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("document_type", "invoice");
      appendDocumentFilterParams(params, billingDocumentFilters);
      const response = await axios.get(`${apiBaseUrl}/admin/billing-documents/export?${params.toString()}`, {
        responseType: "blob",
      });
      const filename = getBillingDownloadFilename(response, `請求書管理一覧_${billingDocumentFilters.month || "all"}.xlsx`);
      triggerBlobDownload(
        new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        filename,
      );
    } catch (error) {
      console.error("請求書一覧の導出に失敗:", error);
      alert("請求書一覧の導出に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateManualInvoice = async (payload) => {
    setLoading(true);
    try {
      const response = await axios.post(`${apiBaseUrl}/admin/billing-documents/manual`, payload, {
        responseType: "blob",
      });
      const filename = getBillingDownloadFilename(response, "手动请求书.pdf");
      triggerBlobDownload(new Blob([response.data], { type: "application/pdf" }), filename);
      await fetchBillingDocuments();
      alert("请求书生成成功");
      return true;
    } catch (error) {
      console.error("手动请求书生成失败:", error);
      const message = await getBlobErrorMessage(error, "请求书生成失败");
      alert(message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleCreateManualReceipt = async (payload) => {
    setLoading(true);
    try {
      const response = await axios.post(`${apiBaseUrl}/admin/billing-documents/manual-receipt`, payload, {
        responseType: "blob",
      });
      const filename = getBillingDownloadFilename(response, "手动領収書.pdf");
      triggerBlobDownload(new Blob([response.data], { type: "application/pdf" }), filename);
      await fetchReceiptDocuments();
      alert("領収書生成成功");
      return true;
    } catch (error) {
      console.error("手动領収書生成失败:", error);
      const message = await getBlobErrorMessage(error, "領収書生成失败");
      alert(message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSearchReceiptOrders = async (keyword) => {
    if (!keyword.trim()) return [];
    const response = await axios.get(`${apiBaseUrl}/admin/orders/search`, {
      params: { q: keyword.trim() },
    });
    return response.data?.orders || [];
  };

  const handleCreateCombinedOrderReceipt = async (payload) => {
    setLoading(true);
    try {
      const response = await axios.post(`${apiBaseUrl}/admin/billing-documents/order-receipts/combined`, payload, {
        responseType: "blob",
      });
      const filename = getBillingDownloadFilename(response, "合并領収書.pdf");
      triggerBlobDownload(new Blob([response.data], { type: "application/pdf" }), filename);
      await fetchReceiptDocuments();
      alert("合并領収書生成成功");
      return true;
    } catch (error) {
      console.error("合并領収書生成失败:", error);
      const message = await getBlobErrorMessage(error, "合并領収書生成失败");
      alert(message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSingleOrderReceipts = async (payload) => {
    setLoading(true);
    try {
      const response = await axios.post(`${apiBaseUrl}/admin/billing-documents/order-receipts/single`, payload, {
        responseType: "blob",
      });
      const filename = getBillingDownloadFilename(response, "逐单領収書.zip");
      triggerBlobDownload(new Blob([response.data], { type: "application/zip" }), filename);
      await fetchReceiptDocuments();
      alert("逐单領収書生成成功");
      return true;
    } catch (error) {
      console.error("逐单領収書生成失败:", error);
      const message = await getBlobErrorMessage(error, "逐单領収書生成失败");
      alert(message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    billingDocumentFilters,
    billingDocumentSummary,
    billingDocuments,
    billingDocumentsLoading,
    receiptDocumentFilters,
    receiptDocumentSummary,
    receiptDocuments,
    receiptDocumentsLoading,
    fetchBillingDocuments,
    fetchReceiptDocuments,
    handleBillingDocumentFiltersChange,
    handleReceiptDocumentFiltersChange,
    handleDeleteBillingDocument,
    handleCreateManualInvoice,
    handleCreateManualReceipt,
    handleCreateCombinedOrderReceipt,
    handleCreateSingleOrderReceipts,
    handleSearchReceiptOrders,
    handleDownloadBillingDocument,
    handleExportBillingDocuments,
    handleRestoreBillingDocument,
  };
}
